import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import { getUserFromFirestore } from '../../shared/api/firebase';
import { Loader, ShieldAlert } from 'lucide-react';

/**
 * 관리자 전용 보호된 라우트 컴포넌트
 * - 로그인하지 않은 사용자: 홈으로 리다이렉트
 * - 로그인했지만 관리자가 아닌 사용자: 접근 거부 화면 표시 후 홈으로 리다이렉트
 * - 관리자: 정상적으로 컴포넌트 렌더링
 */
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { currentUser } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  useEffect(() => {
    const checkAuthorization = async () => {
      // 로그인하지 않은 경우
      if (!currentUser) {
        setIsChecking(false);
        setIsAuthorized(false);
        return;
      }

      // 관리자 권한이 필요한 경우
      if (requireAdmin) {
        try {
          const result = await getUserFromFirestore(currentUser.uid);
          
          if (result.success && result.data && result.data.isAdmin === true) {
            // 관리자 확인 완료
            setIsAuthorized(true);
            setIsChecking(false);
          } else {
            // 관리자가 아님 - 접근 거부
            console.warn('⚠️ 관리자 권한이 없는 사용자의 접근 시도:', currentUser.email);
            setIsAuthorized(false);
            setShowAccessDenied(true);
            
            // 3초 후 홈으로 리다이렉트
            setTimeout(() => {
              setIsChecking(false);
            }, 3000);
          }
        } catch (error) {
          console.error('❌ 권한 확인 오류:', error);
          setIsAuthorized(false);
          setShowAccessDenied(true);
          
          // 3초 후 홈으로 리다이렉트
          setTimeout(() => {
            setIsChecking(false);
          }, 3000);
        }
      } else {
        // 관리자 권한이 필요하지 않은 경우 (일반 로그인만 필요)
        setIsAuthorized(true);
        setIsChecking(false);
      }
    };

    checkAuthorization();
  }, [currentUser, requireAdmin]);

  // 권한 확인 중
  if (isChecking && !showAccessDenied) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--gray-50)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Loader 
            size={48} 
            style={{ 
              color: 'var(--primary)',
              animation: 'spin 1s linear infinite'
            }} 
          />
          <p style={{
            fontSize: '16px',
            color: 'var(--gray-600)',
            fontWeight: '500'
          }}>
            권한을 확인하는 중...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // 접근 거부 화면 표시
  if (showAccessDenied) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '48px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80px',
            height: '80px',
            background: '#FEE2E2',
            borderRadius: '50%',
            marginBottom: '24px'
          }}>
            <ShieldAlert size={40} style={{ color: '#DC2626' }} />
          </div>
          
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1F2937',
            marginBottom: '12px'
          }}>
            접근 권한이 없습니다
          </h1>
          
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            marginBottom: '8px',
            lineHeight: '1.6'
          }}>
            이 페이지는 관리자 전용입니다.
          </p>
          
          <p style={{
            fontSize: '14px',
            color: '#9CA3AF',
            marginBottom: '32px'
          }}>
            잠시 후 홈 화면으로 이동합니다...
          </p>
          
          <div style={{
            width: '100%',
            height: '4px',
            background: '#E5E7EB',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: '#DC2626',
              animation: 'progress 3s linear forwards'
            }} />
          </div>
        </div>
        
        <style>{`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes progress {
            from {
              width: 0%;
            }
            to {
              width: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  // 권한 확인 완료 후 처리
  if (!isAuthorized) {
    // 권한이 없으면 홈으로 리다이렉트
    return <Navigate to="/" replace />;
  }

  // 권한이 있으면 컴포넌트 렌더링
  return <>{children}</>;
};

export default ProtectedRoute;

